import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useIssuers, useBadges, useInfiniteAwards, useCreateAward, useSendAward, useShareAward, useUsage, useRevokeAward, useEditAward, useDeleteAward, useAwardDelivery } from '../hooks/useApi';
import { api } from '../api/client';
import type { AwardListResponse } from '../types';
import { useFormatDate } from '../hooks/useFormatDate';
import { ConfirmModal } from '../components/ConfirmModal';
import { BadgePicker } from '../components/BadgePicker';
import { AwardDetailModal } from '../components/AwardDetailModal';
import { RevokeAwardModal } from '../components/RevokeAwardModal';
import { EditAwardModal } from '../components/EditAwardModal';
import { BulkDeleteModal } from '../components/BulkDeleteModal';
import { LimitWarning, isAtLimit } from '../components/LimitWarning';
import { ActionErrorPanel } from '../components/ActionErrorPanel';
import type { Award, Badge, Issuer } from '../types';
import type { BulkSendJob } from '../types';

// Backend write paths take the GUID in the URL path (full URL breaks API
// Gateway routing because %2F is decoded before route match). Backend
// resolvers handle current + legacy domain reconstruction.
function awardGuid(award: Award): string {
  return award.id.split('/').pop() || award.id;
}

// Per-award email delivery pill — lazy-loads the SES delivery/open status for
// the award's notification email (blank for awards issued before tracking, or
// created without sending an email).
function DeliveryCell({ guid }: { guid: string }) {
  const { data, isLoading } = useAwardDelivery(guid);
  if (isLoading) return <span className="text-[11px] text-gray-300">···</span>;
  if (!data || !data.tracked || !data.status) return <span className="text-[11px] text-gray-300">—</span>;
  const style: Record<string, [string, string]> = {
    open: ['Opened', 'bg-green-100 text-green-700'],
    delivery: ['Delivered', 'bg-blue-100 text-blue-700'],
    send: ['Sent', 'bg-gray-100 text-gray-600'],
    bounce: ['Bounced', 'bg-red-100 text-red-700'],
    complaint: ['Complaint', 'bg-red-100 text-red-700'],
    reject: ['Rejected', 'bg-amber-100 text-amber-700'],
  };
  const [label, cls] = style[data.status] ?? [data.status, 'bg-gray-100 text-gray-500'];
  const ts = data.openedAt || data.deliveredAt || data.sentAt || undefined;
  return (
    <span
      title={ts ? new Date(ts).toLocaleString() : undefined}
      className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}

/** Generic debounce hook — used for the awards search input. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function AwardsPage() {
  const { t } = useTranslation('dashboard');
  const formatDate = useFormatDate();
  const { data: rawIssuers } = useIssuers();
  const { data: rawBadges } = useBadges();
  const issuers = rawIssuers ? (Array.isArray(rawIssuers) ? rawIssuers : ((rawIssuers as any).items ?? [])) as Issuer[] : [];
  const badges = rawBadges ? (Array.isArray(rawBadges) ? rawBadges : ((rawBadges as any).items ?? [])) as Badge[] : [];
  const { mutate: createAward, isPending: creating } = useCreateAward();
  const { mutate: sendAward, isPending: sending } = useSendAward();
  const { mutate: revokeAward } = useRevokeAward();
  const { mutate: editAward, isPending: editing } = useEditAward();
  const { mutate: deleteAward } = useDeleteAward();
  const { data: usage } = useUsage();
  const atAwardLimit = isAtLimit(usage, 'awards');
  const [createError, setCreateError] = useState<Error | null>(null);
  const [lastCreatePayload, setLastCreatePayload] = useState<Record<string, unknown> | null>(null);

  const [issuerFilter, setIssuerFilter] = useState('all');
  const [badgeFilter, setBadgeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState<'name' | 'email'>('name');
  const [showCreate, setShowCreate] = useState(false);
  // Debounced search avoids hitting the backend on every keystroke.
  const debouncedSearch = useDebouncedValue(search, 250);
  const [viewAward, setViewAward] = useState<Award | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Award[] | null>(null);
  const [revokeBulk, setRevokeBulk] = useState(false);
  const [editTarget, setEditTarget] = useState<Award | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkShareOpen, setBulkShareOpen] = useState(false);
  const [bulkSendOpen, setBulkSendOpen] = useState(false);
  const [notEmailedOnly, setNotEmailedOnly] = useState(false);
  const [notEmailedDismissed, setNotEmailedDismissed] = useState(false);
  const { mutate: shareAward } = useShareAward();

  // Create form state
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [issuedDate, setIssuedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expiresDate, setExpiresDate] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState('');

  // Filter badges by selected issuer
  const filteredBadgesForFilter = useMemo(() => {
    if (!badges) return [];
    if (issuerFilter === 'all') return badges;
    return badges.filter((b: Badge) => b.issuer?.id === issuerFilter);
  }, [badges, issuerFilter]);

  // Reset badge filter when issuer changes
  function handleIssuerChange(value: string) {
    setIssuerFilter(value);
    setBadgeFilter('all');
  }

  // Awards query filter — sent server-side. searchField selects the GSI
  // (USER_GUID-RECIPIENT-index for name, USER_GUID-RECIPIENT_EMAIL-index for email).
  const awardsFilter = useMemo(() => {
    const f: { badgeId?: string; search?: string; searchField?: 'name' | 'email'; notEmailedOnly?: boolean } = {};
    if (badgeFilter !== 'all') f.badgeId = badgeFilter;
    if (debouncedSearch.trim()) {
      f.search = debouncedSearch.trim();
      f.searchField = searchField;
    }
    if (notEmailedOnly) f.notEmailedOnly = true;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [badgeFilter, debouncedSearch, searchField, notEmailedOnly]);

  const awardsQuery = useInfiniteAwards(awardsFilter);
  const { data: pagesData, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = awardsQuery;
  const awards = useMemo(() => pagesData?.pages.flatMap(p => p.items) ?? [], [pagesData]);

  // Client-side issuer filter applied AFTER server pagination. Only filters
  // the rows already loaded; for precise issuer-scoped results, pick a
  // specific badge in that issuer instead of leaving "All Badges" selected.
  // notEmailedOnly is now a SERVER filter (accurate across all pages), so we only
  // apply the client-side issuer view-filter here.
  const displayedAwards = useMemo(() => {
    return issuerFilter === 'all' ? awards : awards.filter((a: Award) => a.badge?.issuer?.id === issuerFilter);
  }, [awards, issuerFilter]);

  // Exact total matching the current server scope (badge + search + notEmailed),
  // so we can offer a Gmail-style "Select all N" beyond the loaded pages.
  const { data: countData } = useQuery({
    queryKey: ['awards-count', awardsFilter],
    queryFn: () => api.listAwards({ ...(awardsFilter ?? {}), count: true }),
    staleTime: 15_000,
  });
  const matchTotal = countData?.total ?? null;

  // Count of not-yet-emailed credentials in the current badge/search scope,
  // independent of the notEmailedOnly toggle — powers the load-time "send all"
  // prompt so a user who imported credentials but never emailed them (the most
  // common drop-off) sees a one-click path instead of hunting for the action.
  const notEmailedScope = useMemo(() => {
    const f: { badgeId?: string; search?: string; searchField?: 'name' | 'email'; notEmailedOnly: boolean } = { notEmailedOnly: true };
    if (badgeFilter !== 'all') f.badgeId = badgeFilter;
    if (debouncedSearch.trim()) { f.search = debouncedSearch.trim(); f.searchField = searchField; }
    return f;
  }, [badgeFilter, debouncedSearch, searchField]);
  const { data: notEmailedCountData } = useQuery({
    queryKey: ['awards-notemailed-count', notEmailedScope],
    queryFn: () => api.listAwards({ ...notEmailedScope, count: true }),
    staleTime: 30_000,
  });
  const notEmailedTotal = notEmailedCountData?.total ?? null;

  // Materialize the FULL matching set into the selection (server-paged), so the
  // count is unambiguous ("N selected") and the send covers everything, not just
  // the loaded page. Skips revoked / no-email awards (not sendable).
  const [selectingAll, setSelectingAll] = useState(false);
  const navigate = useNavigate();
  async function selectAllMatching() {
    setSelectingAll(true);
    try {
      const ids = new Set<string>();
      let cursor: string | undefined;
      let guard = 0;
      do {
        const page: AwardListResponse = await api.listAwards(awardsFilter, cursor);
        for (const a of page.items) if (!a.revoked && a.recipientEmail) ids.add(a.id);
        cursor = page.lastEvaluatedKey;
      } while (cursor && guard++ < 1000);
      setSelectedIds(ids);
    } finally { setSelectingAll(false); }
  }

  // One-click path for the load-time prompt: page every not-yet-emailed award
  // into the selection and open the bulk-send modal. Independent of the toggle,
  // so it works whatever the current view. Skips revoked / no-email awards.
  async function sendAllNotEmailed() {
    setSelectingAll(true);
    try {
      // Server resolves the FULL not-emailed set (Phase 2) — the browser never
      // pages ids, so the send can't silently cover only the visible page — and
      // starts a durable job we hand off to the tracked Bulk Sends view.
      const res = await api.startBulkSend({ filter: notEmailedScope });
      navigate(`/bulk-sends/${res.jobId}`);
    } catch (e) {
      console.error('bulk send (not emailed) failed', e);
    } finally { setSelectingAll(false); }
  }

  // Any loaded row selected but more exist beyond the page → offer select-all.
  const allLoadedSelected = displayedAwards.length > 0 && displayedAwards.every((a: Award) => selectedIds.has(a.id));
  const moreThanLoaded = (matchTotal ?? 0) > selectedIds.size;


  function openCreate() {
    setRecipientName('');
    setRecipientEmail('');
    setIssuedDate(new Date().toISOString().split('T')[0]);
    setExpiresDate('');
    setSendEmail(true);
    setSelectedBadge('');
    setShowCreate(true);
  }

  function handleCreate() {
    if (!selectedBadge || recipientName.trim().length < 5 || !recipientEmail.trim()) return;
    setCreateError(null);
    // Mirror the backend's recipient-email check so a malformed address is caught
    // inline instead of round-tripping to a 400 ("invalid recipient email").
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())) {
      setCreateError(new Error('Enter a valid recipient email address.'));
      return;
    }
    const payload = {
      badgeId: selectedBadge,
      recipient: { name: recipientName.trim(), email: recipientEmail.trim() },
      issuedOn: new Date(issuedDate).getTime(),
      expires: expiresDate ? new Date(expiresDate).getTime() : undefined,
    };
    setLastCreatePayload(payload);
    createAward(
      payload,
      {
        onSuccess: (data) => {
          if (sendEmail && data.awardId) {
            // Award_create returns the full Open Badge URL
            // (https://.../certify-badge/award/<guid>); the /awards/{guid}/send
            // route expects just the guid — passing the full URL caused
            // API Gateway to 404 silently (no Lambda invoke, no email).
            const guid = data.awardId.split('/').pop() || data.awardId;
            sendAward({ awardId: guid, email: recipientEmail.trim() });
          }
          setShowCreate(false);
        },
        onError: (err) => setCreateError(err instanceof Error ? err : new Error(String(err))),
      }
    );
  }

  function handleSendEmail(award: Award) {
    // recipient.identity is a SHA256 hash per OB v2 — never use that as the
    // delivery address. recipientEmail is the plaintext we stored alongside.
    const email = award.recipientEmail || '';
    if (!email) return;
    setConfirmAction({
      title: t('awards.sendEmailTitle', 'Send Award Email'),
      message: t('awards.sendEmailMessage', 'Resend the award notification email to {{email}}?', { email }),
      onConfirm: () => {
        sendAward({ awardId: awardGuid(award), email });
        setConfirmAction(null);
      },
    });
  }

  function handleRevoke(award: Award) {
    setViewAward(null);
    setRevokeBulk(false);
    setRevokeTarget([award]);
  }

  // Edit-before-send — only reachable for unsent, non-revoked credentials (the
  // detail modal gates the entry point). Opens the edit form over the detail view.
  function handleEditAward(award: Award) {
    setEditError(null);
    setEditTarget(award);
  }

  function doEdit(fields: { name?: string; email?: string; issuedOn?: string; expires?: string | null }) {
    if (!editTarget) return;
    setEditError(null);
    editAward(
      { awardId: awardGuid(editTarget), fields },
      {
        onSuccess: () => { setEditTarget(null); setViewAward(null); },
        onError: (e: unknown) => setEditError(e instanceof Error ? e.message : t('awards.editFailed', 'Could not save changes. Please try again.')),
      }
    );
  }

  // Bulk revoke acts on the WHOLE selection via a durable server-side job — the
  // worker revokes every selected id and skips already-revoked ones, so it can
  // never stop at the visible page (the old client forEach did). The modal just
  // collects the reason + notify choice.
  function handleBulkRevoke() {
    if (selectedIds.size === 0) return;
    setRevokeBulk(true);
    setRevokeTarget([]); // opens the modal; the actual ids come from selectedIds
  }

  async function doRevoke(reason: string, notify: boolean) {
    if (revokeBulk) {
      setBulkBusy(true);
      try {
        const res = await api.startBulkSend({ action: 'revoke', awardIds: [...selectedIds], reason: reason || undefined, notify });
        setRevokeTarget(null); setRevokeBulk(false); setSelectedIds(new Set());
        navigate(`/bulk-sends/${res.jobId}`);
      } catch (e) { console.error('bulk revoke failed', e); }
      finally { setBulkBusy(false); }
      return;
    }
    (revokeTarget || []).forEach((a: Award) => revokeAward({ awardId: awardGuid(a), reason: reason || undefined, notify }));
    setRevokeTarget(null);
  }

  async function doBulkDelete() {
    setBulkBusy(true);
    try {
      const res = await api.startBulkSend({ action: 'delete', awardIds: [...selectedIds] });
      setBulkDeleteOpen(false); setSelectedIds(new Set());
      navigate(`/bulk-sends/${res.jobId}`);
    } catch (e) { console.error('bulk delete failed', e); }
    finally { setBulkBusy(false); }
  }

  function handleDeleteAward(award: Award) {
    setConfirmAction({
      title: t('awards.deleteTitle', 'Delete credential'),
      message: t('awards.deleteMessage', 'Permanently delete this credential to {{name}}? Its public page will stop working. This cannot be undone and does not refund the credit. To keep an audit trail, use Revoke instead.', { name: award.recipient?.name || '' }),
      onConfirm: () => {
        setConfirmAction(null);
        setViewAward(null);
        deleteAward(awardGuid(award));
      },
    });
  }

  async function exportCSV() {
    setExporting(true);
    try {
      // Page through the FULL matching set server-side (not just the loaded page),
      // then apply the same client-side issuer filter the table shows.
      const all: Award[] = [];
      let cursor: string | undefined = undefined;
      let guard = 0;
      do {
        const page: AwardListResponse = await api.listAwards(awardsFilter, cursor);
        all.push(...(page.items || []));
        cursor = page.lastEvaluatedKey;
      } while (cursor && guard++ < 5000);
      const scoped = issuerFilter === 'all' ? all : all.filter((a) => a.badge?.issuer?.id === issuerFilter);
      const rows = scoped.map((a: Award) => ({
        'Valid From': a.issuedOn ? new Date(a.issuedOn).toISOString().split('T')[0] : '',
        'Valid To': a.expires ? new Date(a.expires).toISOString().split('T')[0] : '',
        'Recipient Name': a.recipient?.name || '',
        'Issuer Name': a.badge?.issuer?.name || '',
        'Badge Name': a.badge?.name || '',
      }));
      if (rows.length === 0) return;
      const cols = Object.keys(rows[0]) as (keyof typeof rows[0])[];
    const csv = [cols.join(','), ...rows.map(r => cols.map(c => `"${String(r[c]).replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `awards-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('export CSV failed', e); }
    finally { setExporting(false); }
  }

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
      <div className="page-wide">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('awards.title', 'Awards')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('awards.subtitle', 'Issue and manage badge awards.')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-xs font-medium text-gray-700 cursor-pointer">
              <input type="checkbox" checked={notEmailedOnly} onChange={e => { setNotEmailedOnly(e.target.checked); setSelectedIds(new Set()); }} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
              {/* Show the count only while the toggle is ON — only then does matchTotal
                  equal the not-yet-emailed total. When off, matchTotal is the whole
                  current-filter scope, so a "(N)" here would misread as the un-emailed count. */}
              {t('awards.notEmailedOnly', 'Not yet emailed')}{notEmailedOnly && matchTotal != null && ` (${matchTotal})`}
            </label>
            <Link to="/bulk-sends" className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-[0.98]">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
              </svg>
              {t('awards.bulkSendsLink', 'Bulk sends')}
            </Link>
            <button onClick={exportCSV} disabled={displayedAwards.length === 0 || exporting} className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-[0.98] disabled:opacity-50">
              {exporting ? t('awards.exportingCSV', 'Exporting…') : t('awards.exportCSV', 'Export CSV')}
            </button>
            <button data-testid="new-award-button" data-track="issue_credential" onClick={openCreate} disabled={atAwardLimit}
              title={atAwardLimit ? "You've reached the monthly award limit on your plan" : undefined}
              className="rounded-xl bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600">
              {t('awards.create', 'Create Award')}
            </button>
          </div>
        </div>

        <LimitWarning usage={usage} resource="awards" />

        {/* Load-time nudge: credentials created but never emailed. The primary
            "Send credential email" action is otherwise hidden behind manual
            row-selection, so a user who bulk-imported recipients often can't
            find how to deliver them. One click here selects every not-yet-
            emailed credential and opens the send modal. */}
        {notEmailedTotal != null && notEmailedTotal > 0 && selectedIds.size === 0 && !notEmailedDismissed && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
            <div className="flex items-start gap-2 text-sm text-primary-900">
              <span aria-hidden>📧</span>
              <span>
                {t('awards.notEmailedBanner', { count: notEmailedTotal })}
                <span className="mt-0.5 block text-xs text-primary-700/90">
                  {t('awards.notEmailedEditHint', 'Need to fix a name, email, or date first? Open a credential and choose Edit — no need to revoke and recreate.')}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-track="bulk_send_all"
                onClick={sendAllNotEmailed}
                disabled={selectingAll}
                className="rounded-lg bg-primary-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50"
              >
                {selectingAll ? t('awards.selecting', 'Selecting…') : t('awards.sendAllNotEmailed', 'Send all {{count}}', { count: notEmailedTotal })}
              </button>
              <button
                onClick={() => setNotEmailedDismissed(true)}
                className="rounded-lg px-2 py-2 text-xs font-medium text-primary-700 hover:bg-primary-100"
                aria-label={t('awards.dismiss', 'Dismiss')}
              >
                &times;
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <select
            value={issuerFilter}
            onChange={e => handleIssuerChange(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          >
            <option value="all">{t('awards.allIssuers', 'All Issuers')}</option>
            {(issuers || []).map((i: Issuer) => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
          <select
            value={badgeFilter}
            onChange={e => setBadgeFilter(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          >
            <option value="all">{t('awards.allBadges', 'All Badges')}</option>
            {filteredBadgesForFilter.map((b: Badge) => (
              <option key={b.id} value={b.id.split('/').pop() || b.id}>{b.name}</option>
            ))}
          </select>
          <div className="flex flex-1 items-stretch rounded-lg border border-gray-200 bg-white focus-within:border-primary-400 focus-within:ring-1 focus-within:ring-primary-400">
            <div role="tablist" className="flex border-r border-gray-200 text-xs font-medium" aria-label={t('awards.searchBy', 'Search by')}>
              <button
                type="button"
                role="tab"
                aria-selected={searchField === 'name'}
                onClick={() => setSearchField('name')}
                className={`px-3 ${searchField === 'name' ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                {t('awards.searchByName', 'Name')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={searchField === 'email'}
                onClick={() => setSearchField('email')}
                className={`px-3 border-l border-gray-200 ${searchField === 'email' ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                {t('awards.searchByEmail', 'Email')}
              </button>
            </div>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={searchField === 'email'
                ? t('awards.searchEmailPlaceholder', 'Search by recipient email (prefix)...')
                : t('awards.searchNamePlaceholder', 'Search by recipient name (prefix)...')}
              className="flex-1 bg-transparent px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="px-3 text-gray-400 hover:text-gray-600"
                title={t('awards.clearSearch', 'Clear')}
              >
                &times;
              </button>
            )}
          </div>
        </div>

        {/* Bulk-selection toolbar */}
        {selectedIds.size > 0 && (
          <div className="mb-3 flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-xs">
            <span className="text-primary-800 font-medium">
              {t('awards.bulkSelected', '{{count}} selected', { count: selectedIds.size })}
            </span>
            <div className="flex gap-2">
              <button onClick={() => setBulkSendOpen(true)} disabled={selectedIds.size === 0}
                className="rounded bg-primary-600 px-3 py-1 font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
                {t('awards.bulkSend', 'Send credential email')} ({selectedIds.size})
              </button>
              <button onClick={() => setBulkShareOpen(true)} className="rounded border border-primary-200 bg-white px-3 py-1 font-semibold text-primary-700 hover:bg-primary-50">
                {t('awards.bulkShare', 'Bulk share via email')}
              </button>
              <button onClick={handleBulkRevoke} className="rounded border border-red-200 bg-white px-3 py-1 font-semibold text-red-600 hover:bg-red-50">
                {t('awards.bulkRevoke', 'Revoke selected')}
              </button>
              <button onClick={() => setBulkDeleteOpen(true)} className="rounded border border-red-300 bg-white px-3 py-1 font-semibold text-red-700 hover:bg-red-50">
                {t('awards.bulkDelete', 'Delete selected')}
              </button>
              <button onClick={() => setSelectedIds(new Set())} className="rounded border border-gray-200 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50">
                {t('common:buttons.clear', 'Clear')}
              </button>
            </div>
          </div>
        )}

        {/* Gmail-style "select all matching" banner — page selection vs. all N */}
        {selectedIds.size > 0 && allLoadedSelected && moreThanLoaded && (
          <div className="mb-3 flex flex-wrap items-center justify-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <svg className="h-5 w-5 flex-none text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
            <span>{t('awards.pageSelectedOnly', 'Only the {{n}} on this page are selected — {{more}} more match your filter.', { n: selectedIds.size, more: (matchTotal ?? 0) - selectedIds.size })}</span>
            <button onClick={selectAllMatching} disabled={selectingAll} className="flex-none rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-amber-700 disabled:opacity-50">
              {selectingAll
                ? t('awards.selecting', 'Selecting…')
                : `${t('awards.selectAll', 'Select all')} ${matchTotal}${notEmailedOnly ? ` ${t('awards.notEmailedWord', 'not-yet-emailed')}` : ''}`}
            </button>
          </div>
        )}
        {selectedIds.size > 0 && matchTotal != null && selectedIds.size >= matchTotal && matchTotal > displayedAwards.length && (
          <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-center text-xs text-primary-800">
            {t('awards.allMatchingSelected', 'All {{n}} matching credentials are selected.', { n: matchTotal })}
          </div>
        )}

        {/* Awards table */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-14 rounded-2xl border border-gray-200 bg-white animate-pulse" />
            ))}
          </div>
        ) : displayedAwards.length > 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white shadow-card overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={displayedAwards.length > 0 && displayedAwards.every((a: Award) => selectedIds.has(a.id))}
                      onChange={e => {
                        if (e.target.checked) setSelectedIds(new Set(displayedAwards.map((a: Award) => a.id)));
                        else setSelectedIds(new Set());
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">{t('awards.issuedDate', 'Issued')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">{t('awards.expires', 'Expires')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">{t('awards.recipient', 'Recipient')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">{t('awards.badge', 'Badge')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">{t('awards.email', 'Email')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody>
                {displayedAwards.map((award: Award) => (
                  <tr
                    key={award.id}
                    onClick={() => setViewAward(award)}
                    className="border-b border-gray-50 hover:bg-gray-50/70 transition-colors cursor-pointer"
                  >
                    <td className="px-3 py-3 w-8" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(award.id)}
                        onChange={e => {
                          const next = new Set(selectedIds);
                          if (e.target.checked) next.add(award.id); else next.delete(award.id);
                          setSelectedIds(next);
                        }}
                      />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">{award.issuedOn ? formatDate(award.issuedOn) : '--'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{award.expires ? formatDate(award.expires) : '--'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {award.recipient?.name || '--'}
                      {award.revoked && (
                        <span className="ml-2 inline-flex items-center rounded-md bg-red-50 border border-red-200 px-1.5 py-0.5 text-[10px] font-semibold text-red-600 align-middle">
                          {t('awards.revokedChip', 'Revoked')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{award.badge?.name || '--'}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}><DeliveryCell guid={awardGuid(award)} /></td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setViewAward(award); }}
                        className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                      >
                        {t('awards.view', 'View')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-card">
            <p className="text-sm text-gray-400">
              {awardsFilter
                ? t('awards.noAwardsFiltered', 'No awards match the current filters.')
                : t('awards.noAwards', 'No awards yet. Create your first award to get started.')}
            </p>
          </div>
        )}

        {/* Pagination footer — Load more button + cumulative count */}
        {!isLoading && displayedAwards.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
            <span>
              {issuerFilter !== 'all' && displayedAwards.length !== awards.length
                ? t('awards.showingFiltered', 'Showing {{shown}} of {{loaded}} loaded', { shown: displayedAwards.length, loaded: awards.length })
                : t('awards.showing', 'Showing {{count}}', { count: displayedAwards.length })}
              {hasNextPage ? t('awards.moreAvailable', ' — more available') : t('awards.endOfList', ' (end)')}
            </span>
            {hasNextPage && (
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                {isFetchingNextPage ? t('awards.loadingMore', 'Loading…') : t('awards.loadMore', 'Load more')}
              </button>
            )}
          </div>
        )}
      </div>

      {viewAward && (
        <AwardDetailModal
          award={viewAward}
          onClose={() => setViewAward(null)}
          viewMode="owner"
          onResend={handleSendEmail}
          resendPending={sending}
          onEdit={handleEditAward}
          onRevoke={handleRevoke}
          onDelete={handleDeleteAward}
        />
      )}

      {editTarget && (
        <EditAwardModal
          award={editTarget}
          onSave={doEdit}
          onCancel={() => { setEditTarget(null); setEditError(null); }}
          pending={editing}
          error={editError}
        />
      )}

      {/* Create award modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowCreate(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900">{t('awards.createAward', 'Create Award')}</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('awards.badge', 'Badge')}</label>
                <BadgePicker
                  badges={badges}
                  selected={selectedBadge}
                  onSelect={setSelectedBadge}
                  placeholder={t('awards.selectBadge', 'Select a badge...')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('awards.recipientName', 'Recipient Name')}</label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={e => setRecipientName(e.target.value)}
                  placeholder={t('awards.recipientNamePlaceholder', 'Full name (min 5 characters)')}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
                {recipientName.length > 0 && recipientName.trim().length < 5 && (
                  <p className="mt-1 text-xs text-red-500">{t('awards.nameMinLength', 'Name must be at least 5 characters.')}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('awards.recipientEmail', 'Recipient Email')}</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={e => setRecipientEmail(e.target.value)}
                  placeholder={t('awards.recipientEmailPlaceholder', 'recipient@example.com')}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('awards.issuedDate', 'Issued')}</label>
                  <input
                    type="date"
                    value={issuedDate}
                    onChange={e => setIssuedDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('awards.expiresOptional', 'Expires (optional)')}</label>
                  <input
                    type="date"
                    value={expiresDate}
                    onChange={e => setExpiresDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  />
                </div>
              </div>
              <div className="space-y-3 pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="text-sm text-gray-700">{t('awards.sendEmailOnCreate', 'Send email notification')}</span>
                </label>
              </div>
              <ActionErrorPanel
                error={createError}
                context={{ resource: 'award', action: 'create', payload: lastCreatePayload ?? undefined }}
                onRetry={() => { setCreateError(null); handleCreate(); }}
                onDismiss={() => setCreateError(null)}
              />
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowCreate(false)} className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                  {t('common:buttons.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating || !selectedBadge || recipientName.trim().length < 5 || !recipientEmail.trim()}
                  className="rounded-lg bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {creating ? t('awards.creating', 'Creating...') : t('awards.create', 'Create Award')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction?.title ?? ''}
        message={confirmAction?.message ?? ''}
        confirmLabel={t('awards.confirmSend', 'Send')}
        variant="default"
        onConfirm={() => confirmAction?.onConfirm()}
        onCancel={() => setConfirmAction(null)}
      />

      <RevokeAwardModal
        open={!!revokeTarget}
        count={revokeBulk ? selectedIds.size : (revokeTarget?.length ?? 0)}
        recipientName={!revokeBulk && revokeTarget?.length === 1 ? revokeTarget[0].recipient?.name : undefined}
        onConfirm={doRevoke}
        onCancel={() => { setRevokeTarget(null); setRevokeBulk(false); }}
      />

      {bulkDeleteOpen && (
        <BulkDeleteModal
          count={selectedIds.size}
          busy={bulkBusy}
          onConfirm={doBulkDelete}
          onClose={() => setBulkDeleteOpen(false)}
        />
      )}

      {bulkShareOpen && (
        <BulkShareModal
          awards={displayedAwards.filter((a: Award) => selectedIds.has(a.id))}
          onClose={() => setBulkShareOpen(false)}
          onComplete={() => { setBulkShareOpen(false); setSelectedIds(new Set()); }}
          shareAward={shareAward}
        />
      )}

      {bulkSendOpen && (
        <BulkSendModal
          awardIds={Array.from(selectedIds)}
          onClose={() => setBulkSendOpen(false)}
          onComplete={() => { setBulkSendOpen(false); setSelectedIds(new Set()); void awardsQuery.refetch(); }}
        />
      )}
    </div>
  );
}

/* ---------- Bulk send modal (TICKET-1b) ---------- */
function BulkSendModal({
  awardIds,
  onClose,
  onComplete,
}: {
  awardIds: string[];
  onClose: () => void;
  onComplete: () => void;
}) {
  const { t } = useTranslation('dashboard');
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<BulkSendJob | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resend, setResend] = useState(false);

  const total = job?.total ?? awardIds.length;
  const processed = job ? job.sent + job.failed + job.skipped : 0;
  const finished = job?.status === 'done' || job?.status === 'failed';
  const pct = total ? Math.round((processed / total) * 100) : 0;

  async function start() {
    setStarting(true); setError(null);
    try {
      const res = await api.startBulkSend({ awardIds, resend });
      setJobId(res.jobId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start the send');
    } finally { setStarting(false); }
  }

  // Poll the job until it finishes. Server-side + durable: closing the tab does
  // not stop the send; reopening would just re-poll a fresh job.
  useEffect(() => {
    if (!jobId) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const j = await api.getBulkSendJob(jobId);
        if (!active) return;
        setJob(j);
        if (j.status === 'done' || j.status === 'failed') return;
      } catch { /* transient — keep polling */ }
      if (active) timer = setTimeout(poll, 2000);
    };
    timer = setTimeout(poll, 800);
    return () => { active = false; clearTimeout(timer); };
  }, [jobId]);

  function downloadErrors() {
    const csv = 'award,error\n' + (job?.errors ?? []).map(e => `"${String(e).replace(/"/g, '""')}"`).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'bulk-send-errors.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">
            {t('awards.bulkSendTitle', 'Send credential email')} — {awardIds.length} {t('awards.recipients', 'recipient(s)')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>

        {!jobId ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('awards.bulkSendIntro2', 'Each recipient gets their own credential email with a private verification link. This runs on our servers — you can close the tab and it keeps going. Already-emailed and revoked awards are skipped automatically.')}
            </p>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <label className="flex items-start gap-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={resend}
                onChange={(e) => setResend(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>
                {t('awards.resendLabel', 'Also re-send to recipients already emailed')}
                <span className="mt-0.5 block text-xs text-gray-400">
                  {t('awards.resendHelp', 'Off by default, so no one is emailed twice by accident.')}
                </span>
              </span>
            </label>
            {awardIds.length === 0 ? (
              <p className="text-sm text-amber-700">{t('awards.bulkSendEmpty', 'No recipients selected.')}</p>
            ) : (
              <button onClick={start} disabled={starting} className="w-full rounded-xl bg-primary-600 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
                {starting ? t('awards.starting', 'Starting…') : t('awards.bulkSendStart', 'Send {{count}} emails', { count: awardIds.length })}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="mb-1 flex justify-between text-xs text-gray-500">
                <span>{processed} / {total}</span>
                <span>
                  {t('awards.sentCount', '{{n}} sent', { n: job?.sent ?? 0 })} · {t('awards.failedCount', '{{n}} failed', { n: job?.failed ?? 0 })}
                  {(job?.skipped ?? 0) > 0 && ` · ${t('awards.skippedCount', '{{n}} skipped', { n: job?.skipped ?? 0 })}`}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div className={`h-full transition-all ${finished ? 'bg-green-500' : 'bg-primary-600'}`} style={{ width: `${pct}%` }} />
              </div>
            </div>

            <p className="text-xs text-gray-500">
              {!finished && `${t('awards.bulkSendRunning', 'This runs on our servers — you can close this window and it keeps going.')} `}
              {jobId && (
                <Link to={`/bulk-sends/${jobId}`} className="font-medium text-primary-600 hover:underline">
                  {t('awards.trackInBulkSends', 'Open in Bulk sends →')}
                </Link>
              )}
            </p>

            {(job?.errors?.length ?? 0) > 0 && (
              <div className="max-h-40 overflow-y-auto rounded-lg border border-red-100 bg-red-50/50 p-2 text-xs text-red-700">
                {job!.errors.slice(0, 50).map((e, i) => <div key={i} className="truncate">{e}</div>)}
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              {(job?.errors?.length ?? 0) > 0 && (
                <button onClick={downloadErrors} className="mr-auto rounded border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                  {t('awards.downloadFailures', 'Download failures')}
                </button>
              )}
              {finished ? (
                <button onClick={onComplete} className="rounded-xl bg-primary-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-700">{t('awards.done', 'Done')}</button>
              ) : (
                <button onClick={onClose} className="rounded-xl border border-gray-200 px-4 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">{t('awards.close', 'Close')}</button>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Bulk share modal ---------- */
function BulkShareModal({
  awards,
  onClose,
  onComplete,
  shareAward,
}: {
  awards: Award[];
  onClose: () => void;
  onComplete: () => void;
  shareAward: (
    vars: { awardId: string; recipients: string; subject: string; message: string },
    opts?: { onSuccess?: () => void; onError?: (err: Error) => void }
  ) => void;
}) {
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('Badges from our team');
  const [message, setMessage] = useState('Sharing some recent achievements:');
  const [progress, setProgress] = useState(0);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function awardGuid(a: Award): string { return a.id.split('/').pop() || a.id; }

  async function send() {
    setError(null);
    const list = recipients.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    if (list.length === 0) { setError('Add at least one recipient.'); return; }
    if (list.length > 20) { setError('Up to 20 recipients per send.'); return; }
    setRunning(true);
    setProgress(0);
    let done = 0;
    for (const award of awards) {
      await new Promise<void>(resolve => {
        shareAward(
          { awardId: awardGuid(award), recipients: list.join(','), subject, message },
          { onSuccess: () => resolve(), onError: () => resolve() }
        );
      });
      done += 1;
      setProgress(done);
    }
    setRunning(false);
    onComplete();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={!running ? onClose : undefined} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Bulk share — {awards.length} award(s)</h3>
          <button onClick={onClose} disabled={running} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Recipients (comma or space separated, up to 20)</label>
            <input type="text" value={recipients} onChange={e => setRecipients(e.target.value)}
              disabled={running}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} disabled={running}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Message</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} disabled={running}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400" />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {running && (
            <div className="text-xs text-gray-600">
              Sending {progress} / {awards.length}…
              <div className="mt-1 h-1.5 rounded bg-gray-100 overflow-hidden">
                <div className="h-full bg-primary-600 transition-all" style={{ width: `${(progress / awards.length) * 100}%` }} />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={running} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={send} disabled={running || !recipients.trim()} className="rounded-lg bg-primary-600 px-4 py-2 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
              {running ? 'Sending…' : `Send to ${awards.length}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
